import { useState } from "react";

const DISCORD_WEBHOOK = "https://discordapp.com/api/webhooks/1513600641820528651/ZjlQRqK6PYCrUiVRlXW9Vml94F5zqbPv2h3UTz0auNCKZ-0UPWYBKMnI7Dc-Q9p3VxS8";

const ISSUE_TYPES = [
  { id: "signal", label: "Signal Error", emoji: "📡", color: "#00D4FF" },
  { id: "execution", label: "Trade Execution", emoji: "⚡", color: "#FF6B35" },
  { id: "data", label: "Data / Price Feed", emoji: "📊", color: "#9B59B6" },
  { id: "ui", label: "UI / Display Bug", emoji: "🖥️", color: "#2ECC71" },
  { id: "alert", label: "Alert Not Firing", emoji: "🔔", color: "#F1C40F" },
  { id: "other", label: "Other", emoji: "💬", color: "#95A5A6" },
];

export default function ReportIssue({ currentSignals = null, onClose }) {
  const [step, setStep] = useState(1); // 1=type, 2=details, 3=sent
  const [issueType, setIssueType] = useState(null);
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  async function submitReport() {
    if (!description.trim()) return;
    setSending(true);
    setError(null);

    const type = ISSUE_TYPES.find(t => t.id === issueType);
    const severityColors = { low: 3066993, medium: 16776960, high: 15158332 };
    const severityLabels = { low: "🟢 Low", medium: "🟡 Medium", high: "🔴 High" };

    const embed = {
      title: `${type.emoji} Bug Report — ${type.label}`,
      color: severityColors[severity],
      fields: [
        { name: "Severity", value: severityLabels[severity], inline: true },
        { name: "Issue Type", value: type.label, inline: true },
        { name: "Time", value: new Date().toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET", inline: true },
        { name: "Description", value: description.trim() },
      ],
      footer: { text: "APEX Scanner Pro — Bug Reporter" },
      timestamp: new Date().toISOString(),
    };

    if (currentSignals) {
      const sigSummary = currentSignals
        .slice(0, 5)
        .map(s => `${s.ticker}: Score ${s.score} | ${s.direction} | $${s.price?.toFixed(2) || "N/A"}`)
        .join("\n");
      embed.fields.push({ name: "Active Signals at Time of Report", value: `\`\`\`${sigSummary}\`\`\`` });
    }

    // FIX 2026-07-26: was sending to an unmonitored Discord webhook - now
    // relays through the backend to Telegram, which is actually checked.
    try {
      const res = await fetch('/api/report-issue', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          issueType: type.label,
          severity,
          description: description.trim(),
          currentSignals: currentSignals ? currentSignals.slice(0, 5) : null,
        }),
      });
      if (res.ok) {
        setStep(3);
      } else {
        setError("Failed to send. Please try again.");
      }
    } catch (e) {
      setError("Network error. Check connection.");
    }
    setSending(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: "linear-gradient(145deg, #0a0e1a, #0d1526)",
        border: "1px solid rgba(0,212,255,0.2)",
        borderRadius: 16, padding: 32, width: 480, maxWidth: "95vw",
        boxShadow: "0 0 60px rgba(0,212,255,0.1), 0 20px 60px rgba(0,0,0,0.5)",
        position: "relative",
      }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "#00D4FF", boxShadow: "0 0 8px #00D4FF",
              }} />
              <span style={{ color: "#00D4FF", fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" }}>
                Apex Support
              </span>
            </div>
            <h2 style={{ color: "#fff", margin: "4px 0 0", fontSize: 20, fontWeight: 700 }}>
              Report an Issue
            </h2>
          </div>
          {onClose && (
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#888", borderRadius: 8, width: 32, height: 32,
              cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
          )}
        </div>

        {/* Step indicator */}
        {step < 3 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                height: 3, flex: 1, borderRadius: 2,
                background: s <= step ? "#00D4FF" : "rgba(255,255,255,0.1)",
                transition: "background 0.3s",
              }} />
            ))}
          </div>
        )}

        {/* Step 1 — Issue Type */}
        {step === 1 && (
          <div>
            <p style={{ color: "#8899aa", fontSize: 13, marginBottom: 16 }}>
              What type of issue are you experiencing?
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {ISSUE_TYPES.map(type => (
                <button key={type.id} onClick={() => { setIssueType(type.id); setStep(2); }}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid rgba(255,255,255,0.08)`,
                    borderRadius: 10, padding: "12px 14px",
                    cursor: "pointer", textAlign: "left",
                    transition: "all 0.2s", color: "#fff",
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.border = `1px solid ${type.color}44`;
                    e.currentTarget.style.background = `${type.color}11`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.border = "1px solid rgba(255,255,255,0.08)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                  }}
                >
                  <span style={{ fontSize: 20 }}>{type.emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Details */}
        {step === 2 && (
          <div>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(0,212,255,0.05)", border: "1px solid rgba(0,212,255,0.15)",
              borderRadius: 8, padding: "8px 12px", marginBottom: 20,
            }}>
              <span style={{ fontSize: 18 }}>{ISSUE_TYPES.find(t => t.id === issueType)?.emoji}</span>
              <span style={{ color: "#00D4FF", fontSize: 13, fontWeight: 500 }}>
                {ISSUE_TYPES.find(t => t.id === issueType)?.label}
              </span>
              <button onClick={() => setStep(1)} style={{
                marginLeft: "auto", background: "none", border: "none",
                color: "#556", cursor: "pointer", fontSize: 12,
              }}>Change</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: "#8899aa", fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                Severity
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {["low", "medium", "high"].map(s => (
                  <button key={s} onClick={() => setSeverity(s)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer",
                    border: severity === s
                      ? `1px solid ${s === "low" ? "#2ECC71" : s === "medium" ? "#F1C40F" : "#E74C3C"}`
                      : "1px solid rgba(255,255,255,0.08)",
                    background: severity === s
                      ? `${s === "low" ? "#2ECC71" : s === "medium" ? "#F1C40F" : "#E74C3C"}22`
                      : "rgba(255,255,255,0.03)",
                    color: severity === s
                      ? s === "low" ? "#2ECC71" : s === "medium" ? "#F1C40F" : "#E74C3C"
                      : "#556",
                    fontSize: 12, fontWeight: 600, textTransform: "capitalize",
                  }}>{s}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: "#8899aa", fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                Describe the Issue
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What happened? What were you expecting? Include any error messages..."
                style={{
                  width: "100%", height: 110, background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
                  color: "#fff", padding: 12, fontSize: 13, resize: "none",
                  fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box",
                  lineHeight: 1.6,
                }}
                onFocus={e => e.target.style.border = "1px solid rgba(0,212,255,0.3)"}
                onBlur={e => e.target.style.border = "1px solid rgba(255,255,255,0.08)"}
              />
            </div>

            {currentSignals && (
              <div style={{
                background: "rgba(0,212,255,0.03)", border: "1px solid rgba(0,212,255,0.1)",
                borderRadius: 8, padding: "8px 12px", marginBottom: 16,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 14 }}>📎</span>
                <span style={{ color: "#556", fontSize: 12 }}>Current signal data will be attached automatically</span>
              </div>
            )}

            {error && (
              <div style={{ color: "#E74C3C", fontSize: 12, marginBottom: 12 }}>{error}</div>
            )}

            <button
              onClick={submitReport}
              disabled={!description.trim() || sending}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 10,
                background: description.trim() ? "linear-gradient(135deg, #0066FF, #00D4FF)" : "rgba(255,255,255,0.05)",
                border: "none", color: description.trim() ? "#fff" : "#334",
                fontSize: 14, fontWeight: 700, cursor: description.trim() ? "pointer" : "not-allowed",
                letterSpacing: 0.5, transition: "all 0.2s",
              }}
            >
              {sending ? "Sending..." : "Submit Report"}
            </button>
          </div>
        )}

        {/* Step 3 — Confirmation */}
        {step === 3 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "rgba(0,212,255,0.1)", border: "2px solid #00D4FF",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, margin: "0 auto 20px",
              boxShadow: "0 0 30px rgba(0,212,255,0.3)",
            }}>✓</div>
            <h3 style={{ color: "#fff", margin: "0 0 8px", fontSize: 18 }}>Report Submitted</h3>
            <p style={{ color: "#8899aa", fontSize: 13, margin: "0 0 24px", lineHeight: 1.6 }}>
              Your report has been sent to the Apex team via Discord.<br />
              We typically respond within a few hours.
            </p>
            <div style={{
              background: "rgba(0,212,255,0.05)", border: "1px solid rgba(0,212,255,0.15)",
              borderRadius: 10, padding: "12px 16px", marginBottom: 20, textAlign: "left",
            }}>
              <p style={{ color: "#556", fontSize: 12, margin: 0 }}>
                Join our Discord for faster support and trade discussion:
              </p>
              <p style={{ color: "#00D4FF", fontSize: 13, margin: "4px 0 0", fontWeight: 600 }}>
                discord.gg/apextrader
              </p>
            </div>
            {onClose && (
              <button onClick={onClose} style={{
                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff", borderRadius: 10, padding: "10px 32px",
                cursor: "pointer", fontSize: 14,
              }}>Close</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
